import { useRef, type ReactNode, type ComponentProps, useState } from "react";
import { Button, Dialog } from "ui";

export type AlertModalProps = {
  isOpen: boolean;
  onClose(): void;
  title: string;
  description: ReactNode;
  Icon: (props: ComponentProps<"svg">) => JSX.Element;
  variant: "danger" | "warning" | "success";
  confirmLabel: string;
  onCancel?: () => void;
  children?: ReactNode;
  disableButtonVariant?: boolean;
} & (
  | {
      onConfirm: () => void | Promise<void>;
      customConfirmFn?: never;
    }
  | {
      onConfirm?: never;
      customConfirmFn: () => void;
    }
);

export default function AlertModal({
  isOpen,
  onClose,
  title,
  description,
  Icon,
  variant,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
  disableButtonVariant,
  customConfirmFn,
}: AlertModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        if (isLoading) return;
        onClose();
      }}
    >
      <Dialog.Popup className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div
            className={`mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full  sm:mx-0 sm:h-10 sm:w-10 ${iconVariantClassName[variant]}`}
          >
            <Icon className="h-6 w-6 text-current" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Description className="mt-2">
              {description}
            </Dialog.Description>
          </div>
        </div>

        {children && <div className="mb-4 mt-2 px-4 sm:px-6">{children}</div>}

        <Dialog.Footer variant="bare" className="mt-6">
          <Button
            onClick={async (e) => {
              setIsLoading(true);
              try {
                if (customConfirmFn) {
                  await customConfirmFn();
                } else {
                  e.stopPropagation();
                  await onConfirm();
                  onClose();
                }
              } catch (error) {}
              setIsLoading(false);
            }}
            ref={confirmBtnRef}
            className="flex-1"
            variant="primary"
            isLoading={isLoading}
            colorScheme={
              disableButtonVariant || variant !== "danger"
                ? undefined
                : "danger"
            }
          >
            {confirmLabel}
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              onCancel?.();
            }}
            className="flex-1"
            variant="secondary"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
}

const iconVariantClassName: Record<AlertModalProps["variant"], string> = {
  danger: "bg-danger-subtler text-danger",
  warning: "bg-warning-subtler text-warning",
  success: "bg-success-subtler text-success",
};
